/**
 * Athlete Wellness Check-In Screen
 * Submits a daily readiness check-in to the coach.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  TextInput, ActivityIndicator, Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { supabase } from '@/lib/supabase';
import {
  DEFAULT_QUESTIONS,
  submitCheckIn,
  type CheckInQuestion,
} from '@/lib/coachCheckIns';
import { ArrowLeft, CheckCircle } from 'lucide-react-native';

export default function CheckInScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const router = useRouter();
  const { profile } = useAuth();

  const [coachId, setCoachId] = useState<string | null>(null);
  const [loadingCoach, setLoadingCoach] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string | number | boolean>>({});

  // Resolve active coach for this athlete
  const resolveCoach = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const { data } = await supabase
        .from('coach_athlete_links')
        .select('coach_user_id')
        .eq('athlete_user_id', profile.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setCoachId(data?.coach_user_id ?? null);
    } catch (err) {
      console.warn('[CheckIn] resolveCoach error', err);
    } finally {
      setLoadingCoach(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    resolveCoach();
  }, [resolveCoach]);

  const setAnswer = (id: string, value: string | number | boolean) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    if (!profile?.id) {
      Alert.alert('Not signed in', 'Please sign in to submit a check-in.');
      return;
    }
    if (!coachId) {
      Alert.alert('No coach linked', 'You need an active coaching link to submit a check-in.');
      return;
    }
    setSubmitting(true);
    try {
      await submitCheckIn(coachId, profile.id, answers);
      setSubmitted(true);
    } catch (err: any) {
      Alert.alert('Submit failed', err.message || 'Could not submit check-in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingCoach) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <ActivityIndicator color={c.accent} style={{ marginTop: 60 }} />
      </SafeAreaView>
    );
  }

  if (submitted) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <View style={styles.successContainer}>
          <CheckCircle size={64} color={c.accent} />
          <Text style={[styles.successTitle, { color: c.text }]}>Check-In Submitted!</Text>
          <Text style={[styles.successSub, { color: c.textSecondary }]}>
            Your coach will see your responses.
          </Text>
          <TouchableOpacity
            style={[styles.doneBtn, { backgroundColor: c.accent }]}
            onPress={() => router.canGoBack() ? router.back() : router.replace('/(tabs)/home')}
          >
            <Text style={[styles.doneBtnText, { color: c.accentForeground }]}>Done</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.canGoBack() ? router.back() : router.replace('/(tabs)/home')}
          style={styles.backBtn}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: c.text }]}>Daily Check-In</Text>
        <View style={{ width: 30 }} />
      </View>

      {!coachId && (
        <View style={[styles.noCoachBanner, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.noCoachText, { color: c.textSecondary }]}>
            No active coach link found. Ask your coach to connect with you.
          </Text>
        </View>
      )}

      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <Text style={[styles.subtitle, { color: c.textSecondary }]}>
          How are you feeling today?
        </Text>

        {DEFAULT_QUESTIONS.map((q) => (
          <QuestionRow
            key={q.id}
            question={q}
            value={answers[q.id]}
            onChange={(val) => setAnswer(q.id, val)}
            colors={c}
          />
        ))}

        <TouchableOpacity
          style={[
            styles.submitBtn,
            { backgroundColor: c.accent, opacity: !coachId || submitting ? 0.5 : 1 },
          ]}
          onPress={handleSubmit}
          disabled={!coachId || submitting}
        >
          {submitting ? (
            <ActivityIndicator color={c.accentForeground} />
          ) : (
            <Text style={[styles.submitBtnText, { color: c.accentForeground }]}>Submit Check-In</Text>
          )}
        </TouchableOpacity>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

/* ── Question Row ── */
interface QuestionRowProps {
  question: CheckInQuestion;
  value: string | number | boolean | undefined;
  onChange: (val: string | number | boolean) => void;
  colors: any;
}

function QuestionRow({ question, value, onChange, colors: c }: QuestionRowProps) {
  return (
    <View style={[styles.questionCard, { backgroundColor: c.surface, borderColor: c.border }]}>
      <Text style={[styles.questionLabel, { color: c.text }]}>{question.label}</Text>

      {question.type === 'rating' && (
        <View style={styles.ratingRow}>
          {[1, 2, 3, 4, 5].map((n) => (
            <TouchableOpacity
              key={n}
              style={[
                styles.ratingBtn,
                {
                  backgroundColor: value === n ? c.accent : c.surfaceVariant,
                  borderColor: value === n ? c.accent : c.border,
                },
              ]}
              onPress={() => onChange(n)}
            >
              <Text style={[styles.ratingBtnText, { color: value === n ? c.accentForeground : c.text }]}>
                {n}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {question.type === 'yesno' && (
        <View style={styles.yesnoRow}>
          {(['Yes', 'No'] as const).map((opt) => {
            const selected = (opt === 'Yes' && value === true) || (opt === 'No' && value === false);
            return (
              <TouchableOpacity
                key={opt}
                style={[
                  styles.yesnoBtn,
                  {
                    backgroundColor: selected ? c.accent : c.surfaceVariant,
                    borderColor: selected ? c.accent : c.border,
                    flex: 1,
                  },
                ]}
                onPress={() => onChange(opt === 'Yes')}
              >
                <Text style={[styles.yesnoBtnText, { color: selected ? c.accentForeground : c.text }]}>
                  {opt}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {question.type === 'text' && (
        <TextInput
          style={[
            styles.textInput,
            { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border },
          ]}
          placeholder="Type here..."
          placeholderTextColor={c.textTertiary}
          value={typeof value === 'string' ? value : ''}
          onChangeText={(t) => onChange(t)}
          multiline
          numberOfLines={3}
          textAlignVertical="top"
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    justifyContent: 'space-between',
  },
  backBtn: { padding: 4 },
  headerTitle: { fontSize: 20, fontFamily: TYPO.heading.bold },
  scroll: { paddingHorizontal: 16, paddingBottom: 32 },
  subtitle: {
    fontSize: 14,
    fontFamily: TYPO.body.regular,
    marginBottom: 20,
    marginTop: 4,
  },
  questionCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 14,
    gap: 12,
  },
  questionLabel: {
    fontSize: 15,
    fontFamily: TYPO.body.semibold,
  },
  ratingRow: {
    flexDirection: 'row',
    gap: 8,
  },
  ratingBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 2,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ratingBtnText: {
    fontSize: 15,
    fontFamily: TYPO.body.bold,
  },
  yesnoRow: {
    flexDirection: 'row',
    gap: 10,
  },
  yesnoBtn: {
    paddingVertical: 10,
    borderRadius: 2,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  yesnoBtnText: {
    fontSize: 14,
    fontFamily: TYPO.body.bold,
  },
  textInput: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    fontSize: 14,
    fontFamily: TYPO.body.regular,
    minHeight: 80,
  },
  submitBtn: {
    borderRadius: 2,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  submitBtnText: {
    fontSize: 16,
    fontFamily: TYPO.body.bold,
  },
  successContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    paddingHorizontal: 32,
  },
  successTitle: {
    fontSize: 24,
    fontFamily: TYPO.heading.bold,
    textAlign: 'center',
  },
  successSub: {
    fontSize: 15,
    fontFamily: TYPO.body.regular,
    textAlign: 'center',
  },
  doneBtn: {
    borderRadius: 2,
    paddingHorizontal: 40,
    paddingVertical: 14,
    marginTop: 8,
  },
  doneBtnText: {
    fontSize: 16,
    fontFamily: TYPO.body.bold,
  },
  noCoachBanner: {
    marginHorizontal: 16,
    marginBottom: 8,
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
  },
  noCoachText: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    textAlign: 'center',
  },
});
